"use client";

import { Button } from "@/components/ui/button";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { toggleFollowProfileById } from "@/actions/profiles";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useRouter, useSearchParams } from "next/navigation";

type ActionToggleFollowProps = {
  id: string;
  initialIsFollowing?: boolean;
  children?: (
    handleToggleFollow: () => void,
    isLoading: boolean,
    isFollowing: boolean
  ) => ReactNode;
};

export default function ActionToggleFollow({
  id,
  initialIsFollowing = false,
  children,
}: ActionToggleFollowProps) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [isFollowing, isFollowingSet] = useState(initialIsFollowing);
  const [isLoading, isLoadingSet] = useState(false);

  const handleToggleFollow = useCallback(async () => {
    isLoadingSet(true);

    try {
      const result = await toggleFollowProfileById(id);

      if (result.status === "success") {
        isFollowingSet(!isFollowing);
        toast.success(
          isFollowing
            ? "Successfully unfollowed user"
            : "Successfully followed user"
        );
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Failed to update follow status");
      console.error("Toggle follow error:", error);
    } finally {
      isLoadingSet(false);
    }
  }, [id, isFollowing]);

  useEffect(() => {
    const nextAction = searchParams.get("next");

    if (nextAction === `follow-user-${id}`) {
      handleToggleFollow();
      router.replace("?");
    }
  }, [searchParams, handleToggleFollow, id, router]);

  if (children && typeof children === "function") {
    return children(handleToggleFollow, isLoading, isFollowing);
  }

  return (
    <Button
      onClick={handleToggleFollow}
      disabled={isLoading}
      variant={"default"}
      className={cn(
        isFollowing && "bg-blue-500 hover:bg-blue-600",
        "text-white"
      )}
    >
      {isLoading
        ? isFollowing
          ? "Unfollowing..."
          : "Following..."
        : isFollowing
        ? "Unfollow"
        : "Follow"}
    </Button>
  );
}
